import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import BuildGraphMenu from './BuildGraphMenu';
import './BuildGraph.css';
import './QueryExpansion.css';

function QueryExpansionPage() {
  const { graphId } = useParams();

  // Vocabulary state
  const [vocabStatus, setVocabStatus] = useState('loading'); // loading | cached | not_built | error
  const [vocabulary, setVocabulary] = useState(null);
  const [vocabBuiltAt, setVocabBuiltAt] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);

  // Query expansion state
  const [query, setQuery] = useState('');
  const [expanding, setExpanding] = useState(false);
  const [result, setResult] = useState(null);
  const [expandError, setExpandError] = useState(null);

  // Vocabulary inspector toggle
  const [showVocabDetails, setShowVocabDetails] = useState(false);

  // Load vocabulary status on mount
  const loadVocabulary = useCallback(async () => {
    try {
      const data = await apiService.getGraphVocabulary(graphId);
      if (data.success && data.exists) {
        setVocabStatus('cached');
        setVocabulary(data.vocabulary);
        setVocabBuiltAt(data.vocabulary?.built_at);
      } else {
        setVocabStatus('not_built');
        setVocabulary(null);
      }
    } catch (err) {
      console.error('Error loading vocabulary:', err);
      setVocabStatus('error');
    }
  }, [graphId]);

  useEffect(() => {
    loadVocabulary();
  }, [loadVocabulary]);

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      const data = await apiService.buildGraphVocabulary(graphId);
      if (data.success) {
        setVocabStatus('cached');
        setVocabulary(data.vocabulary);
        setVocabBuiltAt(data.vocabulary?.built_at);
      }
    } catch (err) {
      console.error('Rebuild error:', err);
    } finally {
      setRebuilding(false);
    }
  };

  const handleExpand = async () => {
    if (!query.trim()) return;
    setExpanding(true);
    setExpandError(null);
    setResult(null);
    try {
      const data = await apiService.expandQueryPreview(graphId, query.trim());
      if (data.success) {
        setResult(data);
      } else {
        setExpandError(data.detail || 'Expansion failed');
      }
    } catch (err) {
      console.error('Expansion error:', err);
      setExpandError(err.response?.data?.detail || err.message || 'Expansion failed');
    } finally {
      setExpanding(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleExpand();
    }
  };

  return (
    <div className="build-graph-page">
      <BuildGraphMenu />

      <div className="query-expansion">
        {/* Vocabulary Status Card */}
        <section className="query-expansion__section">
          <div className="query-expansion__vocab-header">
            <h3 className="query-expansion__section-title">Vocabulary Context</h3>
            <div className="query-expansion__vocab-actions">
              <span className={`query-expansion__vocab-badge query-expansion__vocab-badge--${vocabStatus}`}>
                {vocabStatus === 'loading' && 'Checking...'}
                {vocabStatus === 'cached' && `Cached${vocabBuiltAt ? ` - ${new Date(vocabBuiltAt).toLocaleString()}` : ''}`}
                {vocabStatus === 'not_built' && 'Not built'}
                {vocabStatus === 'error' && 'Error'}
              </span>
              <button
                className="query-expansion__btn query-expansion__btn--secondary"
                onClick={handleRebuild}
                disabled={rebuilding}
              >
                {rebuilding ? 'Building...' : 'Rebuild Vocabulary'}
              </button>
              {vocabulary && (
                <button
                  className="query-expansion__btn query-expansion__btn--ghost"
                  onClick={() => setShowVocabDetails(!showVocabDetails)}
                >
                  {showVocabDetails ? 'Hide Details' : 'Inspect Vocabulary'}
                </button>
              )}
            </div>
          </div>

          {vocabStatus === 'not_built' && (
            <div className="query-expansion__warning">
              No vocabulary context has been built for this graph yet. Click "Rebuild Vocabulary" or visit the Graph Stats page.
            </div>
          )}

          {/* Vocabulary Inspector */}
          {showVocabDetails && vocabulary && (
            <div className="query-expansion__vocab-inspector">
              <div className="query-expansion__vocab-grid">
                <div className="query-expansion__vocab-card">
                  <h4>Node Types ({vocabulary.node_types?.length || 0})</h4>
                  <div className="query-expansion__tag-list">
                    {(vocabulary.node_types || []).map(t => (
                      <span className="query-expansion__tag" key={t}>{t}</span>
                    ))}
                  </div>
                </div>
                <div className="query-expansion__vocab-card">
                  <h4>Synapse Types ({vocabulary.synapse_types?.length || 0})</h4>
                  <div className="query-expansion__tag-list">
                    {(vocabulary.synapse_types || []).map(t => (
                      <span className="query-expansion__tag query-expansion__tag--edge" key={t}>{t}</span>
                    ))}
                  </div>
                </div>
                <div className="query-expansion__vocab-card">
                  <h4>Node Kinds ({vocabulary.node_kinds?.length || 0})</h4>
                  <div className="query-expansion__tag-list">
                    {(vocabulary.node_kinds || []).map(t => (
                      <span className="query-expansion__tag" key={t}>{t}</span>
                    ))}
                  </div>
                </div>
                <div className="query-expansion__vocab-card">
                  <h4>Synapse Kinds ({vocabulary.synapse_kinds?.length || 0})</h4>
                  <div className="query-expansion__tag-list">
                    {(vocabulary.synapse_kinds || []).map(t => (
                      <span className="query-expansion__tag query-expansion__tag--edge" key={t}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>

              {vocabulary.top_nodes_by_degree?.length > 0 && (
                <div className="query-expansion__vocab-card query-expansion__vocab-card--full">
                  <h4>Top Nodes by Connectivity ({vocabulary.top_nodes_by_degree.length})</h4>
                  <div className="query-expansion__node-list">
                    {vocabulary.top_nodes_by_degree.slice(0, 20).map((n, i) => (
                      <div className="query-expansion__node-item" key={i}>
                        <span className="query-expansion__node-name">{n.name}</span>
                        <span className="query-expansion__node-meta">{n.type} / deg: {n.degree}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {vocabulary.relationship_patterns?.length > 0 && (
                <div className="query-expansion__vocab-card query-expansion__vocab-card--full">
                  <h4>Common Relationship Patterns</h4>
                  <div className="query-expansion__pattern-list">
                    {vocabulary.relationship_patterns.map((p, i) => (
                      <div className="query-expansion__pattern-item" key={i}>
                        <span className="query-expansion__pattern-src">{p.source_type}</span>
                        <span className="query-expansion__pattern-edge">--[{p.edge}]--&gt;</span>
                        <span className="query-expansion__pattern-tgt">{p.target_type}</span>
                        <span className="query-expansion__pattern-count">x{p.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {vocabulary.domain_terms?.length > 0 && (
                <div className="query-expansion__vocab-card query-expansion__vocab-card--full">
                  <h4>Domain Terms ({vocabulary.domain_terms.length})</h4>
                  <div className="query-expansion__tag-list">
                    {vocabulary.domain_terms.map(t => (
                      <span className="query-expansion__tag query-expansion__tag--domain" key={t}>{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Query Input */}
        <section className="query-expansion__section">
          <h3 className="query-expansion__section-title">Test Query Expansion</h3>
          <div className="query-expansion__input-row">
            <textarea
              className="query-expansion__input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a query to see how it would be expanded using the graph vocabulary..."
              rows={3}
              disabled={vocabStatus !== 'cached'}
            />
            <button
              className="query-expansion__btn query-expansion__btn--primary"
              onClick={handleExpand}
              disabled={expanding || !query.trim() || vocabStatus !== 'cached'}
            >
              {expanding ? 'Expanding...' : 'Expand Query'}
            </button>
          </div>

          {expandError && (
            <div className="query-expansion__error">{expandError}</div>
          )}
        </section>

        {/* Results Panel */}
        {result && (
          <section className="query-expansion__section query-expansion__results">
            <h3 className="query-expansion__section-title">Expansion Result</h3>
            <div className="query-expansion__compare">
              <div className="query-expansion__compare-col">
                <div className="query-expansion__compare-label">Original Query</div>
                <div className="query-expansion__compare-text query-expansion__compare-text--original">
                  {result.original_query}
                </div>
              </div>
              <div className="query-expansion__compare-arrow">&#8594;</div>
              <div className="query-expansion__compare-col">
                <div className="query-expansion__compare-label">Expanded Query</div>
                <div className="query-expansion__compare-text query-expansion__compare-text--expanded">
                  {result.expanded_query}
                </div>
              </div>
            </div>
            {result.expansion_notes && (
              <div className="query-expansion__notes">
                <strong>Notes:</strong> {result.expansion_notes}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default QueryExpansionPage;
